import { useState } from "react";
import { 
  FileText, ChartLineUp, TrendUp, TrendDown, ShieldCheck, 
  Lightning, Target, Clock, ArrowDown, Warning
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const sections = [
  {
    id: "sources",
    title: "1. Sources de Donnees",
    content: [
      { label: "CoinGecko API", detail: "100 cryptos + Or (PAXG, XAUT) + Argent (KAG) — Cache 2 min, sparkline 168 pts (7 jours)" },
      { label: "Alpha Vantage", detail: "DAX (via EWG ETF) — 250 jours Daily (outputsize=full pour EMA 200)" },
    ]
  },
  {
    id: "indicators",
    title: "2. Indicateurs Techniques (16)",
    subsections: [
      {
        title: "Day Trading (Crypto / Metaux) — 168 points",
        items: [
          "RSI (14) : Surachete >70 / Survendu <30",
          "MACD (12, 26, 9) : Croisement signal",
          "EMA 9 / EMA 21 : Croisement court terme",
          "MA 20 / MA 50 : Moyennes mobiles simples",
          "Bollinger Bands (20, 2 ecarts-types)",
          "Stochastic RSI (14, 14) : K%/D% extremes",
          "VWAP : Reference intrajournaliere",
          "OBV : Confirmation volume",
          "ATR (14) : Volatilite",
          "Support / Resistance : High/Low 20 periodes",
        ]
      },
      {
        title: "Swing Trading (Indices) — 250 points Daily",
        items: [
          "EMA 50 / EMA 200 : Golden/Death Cross",
          "ADX (14) : Force tendance (>25 forte)",
          "Fibonacci (23.6%, 38.2%, 50%, 61.8%) via Williams Fractal",
        ]
      }
    ]
  },
  {
    id: "trend",
    title: "3. Determination Achat / Vente",
    rules: [
      { type: "crypto", label: "Cryptos (EMA 9/21)", bullish: "Prix > EMA9 ET EMA9 > EMA21", bearish: "Prix < EMA9 ET EMA9 < EMA21" },
      { type: "index", label: "Indices (EMA 50/200)", bullish: "Prix > EMA50 ET EMA50 > EMA200", bearish: "Prix < EMA50 ET EMA50 < EMA200" },
    ]
  },
  {
    id: "scoring",
    title: "4. Systeme de Scoring (100 points)",
    criteria: [
      { name: "Alignement tendance", max: 20, detail: "EMA alignees=20, croisement serre=15, neutre=8" },
      { name: "Force du volume (OBV)", max: 15, detail: "OBV aligne=15, vol +30%=12, +10%=8" },
      { name: "Zone Support/Resistance", max: 20, detail: "Bollinger extreme=20, milieu=15" },
      { name: "Confirmation pattern", max: 15, detail: "RSI(5) + Stoch(5) + MACD(5)" },
      { name: "Liquidite valide", max: 20, detail: "Large-cap 2-8%=20 / Small-cap 3-20%=20" },
      { name: "Precision timing", max: 10, detail: "RSI<35 + StochK<20 (bull)=10" },
    ],
    thresholds: [
      { min: 0, max: 49, label: "EXCLU", color: "bg-slate-200 text-slate-600" },
      { min: 50, max: 69, label: "IGNORER", color: "bg-red-100 text-red-700" },
      { min: 70, max: 84, label: "SURVEILLER", color: "bg-amber-100 text-amber-700" },
      { min: 85, max: 89, label: "SIGNAL FORT", color: "bg-emerald-100 text-emerald-700" },
      { min: 90, max: 100, label: "PREMIUM", color: "bg-purple-100 text-purple-700" },
    ]
  },
  {
    id: "levels",
    title: "5. Niveaux Entree / Sortie",
  },
  {
    id: "timing",
    title: "6. Detection du Timing",
    timings: [
      { condition: "RSI<35 + Stoch<20 (bullish)", signal: "ENTRER", color: "text-emerald-600" },
      { condition: "RSI>65 + Stoch>80 (bearish)", signal: "ENTRER", color: "text-emerald-600" },
      { condition: "RSI 35-45 (bullish)", signal: "ATTENDRE", color: "text-amber-600" },
      { condition: "RSI 45-70 (bullish)", signal: "SURVEILLER", color: "text-blue-600" },
      { condition: "RSI>70 (bullish) / RSI<30 (bearish)", signal: "TROP TARD", color: "text-red-600" },
    ]
  },
  {
    id: "protection",
    title: "7. Protection Automatique",
  },
  {
    id: "filters",
    title: "8. Filtrage Anti-Faux Signaux",
    items: [
      "24 stablecoins en liste blanche (USDT, USDC, DAI, etc.)",
      "Score minimum 50/100 pour apparaitre",
      "Multi-confirmation requise (jamais 1 seul indicateur)",
      "Volume requis via OBV",
      "Detection 'Trop tard' (mouvement epuise)",
      "Seuils liquidite adaptes large-cap vs small-cap",
    ]
  },
];

export default function Methodology() {
  const [expandedSection, setExpandedSection] = useState("scoring");

  return (
    <div className="space-y-6 max-w-5xl" data-testid="methodology-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Methodologie d'Analyse</h1>
          <p className="text-sm text-slate-500 font-body mt-1">Comment le scanner detecte les opportunites</p>
        </div>
        <a href="/ProTrader_Rapport_v2_Corrige.pdf" target="_blank" rel="noopener noreferrer">
          <Button variant="outline" className="rounded-sm" data-testid="download-pdf-btn">
            <FileText size={16} className="mr-2" />
            Telecharger PDF
          </Button>
        </a>
      </div>

      {/* Corrections banner */}
      <Card className="border-amber-200 bg-amber-50/50 rounded-sm">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Warning size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800 font-body">6 corrections appliquees (v2)</p>
              <p className="text-xs text-amber-600 font-body mt-1">EMA 200 (250 jours Daily) | Fibonacci Williams Fractal | Zone RSI 45-70 | Score min 50 | Stablecoins whitelist | Liquidite par market cap</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trend determination */}
      <Card className="rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
            <TrendUp size={16} className="inline mr-2" />
            Determination Achat / Vente
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {sections[2].rules.map((rule) => (
            <div key={rule.type} className="space-y-2">
              <p className="text-xs font-medium text-slate-500 font-body uppercase">{rule.label}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <TrendUp size={14} className="text-emerald-600" weight="bold" />
                    <span className="text-xs font-bold text-emerald-700">BULLISH (Achat)</span>
                  </div>
                  <p className="text-xs text-emerald-600 font-data">{rule.bullish}</p>
                </div>
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                  <div className="flex items-center gap-1 mb-1">
                    <TrendDown size={14} className="text-red-600" weight="bold" />
                    <span className="text-xs font-bold text-red-700">BEARISH (Vente)</span>
                  </div>
                  <p className="text-xs text-red-600 font-data">{rule.bearish}</p>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Scoring system */}
      <Card className="rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
            <Target size={16} className="inline mr-2" />
            Scoring (100 points)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 mb-4">
            {sections[3].criteria.map((c) => (
              <div key={c.name} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-sm">
                <div className="flex-1">
                  <p className="text-sm font-medium font-body">{c.name}</p>
                  <p className="text-xs text-slate-400 font-body">{c.detail}</p>
                </div>
                <Badge variant="outline" className="font-data text-xs ml-2">{c.max} pts</Badge>
              </div>
            ))}
          </div>
          <div className="border-t pt-3">
            <p className="text-xs text-slate-500 font-body mb-2">Seuils d'action :</p>
            <div className="flex flex-wrap gap-2">
              {sections[3].thresholds.map((t) => (
                <span key={t.label} className={`text-xs font-medium px-2 py-1 rounded-sm ${t.color}`}>
                  {t.min}-{t.max}: {t.label}
                </span>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entry/Exit levels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="rounded-sm border-emerald-200">
          <CardHeader className="pb-2 bg-emerald-50/50">
            <CardTitle className="font-heading text-sm font-bold text-emerald-700">
              <TrendUp size={16} className="inline mr-2" />
              ACHAT (Bollinger)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 pt-3">
            <p className="text-xs font-body"><span className="font-medium">Entree :</span> Bollinger basse -> moyenne</p>
            <p className="text-xs font-body"><span className="font-medium text-red-600">Stop-Loss :</span> Bollinger basse x 0.98</p>
            <p className="text-xs font-body"><span className="font-medium text-emerald-600">TP1 :</span> Bollinger haute</p>
            <p className="text-xs font-body"><span className="font-medium text-emerald-600">TP2 :</span> Bollinger haute +5%</p>
            <p className="text-xs font-body"><span className="font-medium text-emerald-600">TP3 :</span> Bollinger haute +10%</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-red-200">
          <CardHeader className="pb-2 bg-red-50/50">
            <CardTitle className="font-heading text-sm font-bold text-red-700">
              <TrendDown size={16} className="inline mr-2" />
              VENTE (Bollinger)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 pt-3">
            <p className="text-xs font-body"><span className="font-medium">Entree :</span> Bollinger moyenne -> haute</p>
            <p className="text-xs font-body"><span className="font-medium text-red-600">Stop-Loss :</span> Bollinger haute x 1.02</p>
            <p className="text-xs font-body"><span className="font-medium text-emerald-600">TP1 :</span> Bollinger basse</p>
            <p className="text-xs font-body"><span className="font-medium text-emerald-600">TP2 :</span> Bollinger basse -5%</p>
            <p className="text-xs font-body"><span className="font-medium text-emerald-600">TP3 :</span> Bollinger basse -10%</p>
          </CardContent>
        </Card>
      </div>

      {/* Fibonacci for indices */}
      <Card className="rounded-sm border-blue-200">
        <CardHeader className="pb-2 bg-blue-50/50">
          <CardTitle className="font-heading text-sm font-bold text-blue-700">
            <ChartLineUp size={16} className="inline mr-2" />
            Indices : Fibonacci + Williams Fractal
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-3">
          <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm mb-3">
            <p className="text-xs text-amber-700 font-body">Correction v2 : Ancrage par Williams Fractal (5 periodes), minimum 20 barres entre pivots swing high/low</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs font-body">
            <div>
              <p className="font-medium text-emerald-600 mb-1">ACHAT</p>
              <p>Entree : Fib 38.2% -> 50%</p>
              <p>SL : Fib 61.8% x 0.98</p>
              <p>TP : Fib 23.6%, Resistance</p>
            </div>
            <div>
              <p className="font-medium text-red-600 mb-1">VENTE</p>
              <p>Entree : Fib 50% -> 38.2%</p>
              <p>SL : Fib 23.6% x 1.02</p>
              <p>TP : Fib 61.8%, Support</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timing */}
      <Card className="rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
            <Clock size={16} className="inline mr-2" />
            Detection du Timing
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-2 bg-amber-50 border border-amber-200 rounded-sm mb-3">
            <p className="text-xs text-amber-700 font-body">Correction v2 : Zone morte RSI 45-70 eliminee — toute la plage est couverte</p>
          </div>
          <div className="space-y-1">
            {sections[5].timings.map((t, i) => (
              <div key={i} className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-sm">
                <p className="text-xs font-data text-slate-600">{t.condition}</p>
                <span className={`text-xs font-bold ${t.color}`}>{t.signal}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Protection */}
      <Card className="rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
            <ShieldCheck size={16} className="inline mr-2" />
            Protection Automatique
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
              <p className="text-xs font-bold text-red-700 mb-1">Stop-Loss Auto</p>
              <p className="text-[11px] text-red-600 font-body">Prix descend au SL -> trade ferme. Perte limitee.</p>
            </div>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-sm">
              <p className="text-xs font-bold text-emerald-700 mb-1">Take-Profit Auto</p>
              <p className="text-[11px] text-emerald-600 font-body">Prix monte au TP -> trade ferme. Gain securise.</p>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
              <p className="text-xs font-bold text-blue-700 mb-1">Trailing Stop</p>
              <p className="text-[11px] text-blue-600 font-body">SL dynamique suit le prix a la hausse. Configurable en %.</p>
            </div>
          </div>
          <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-sm font-data text-xs text-slate-600">
            <p className="font-medium mb-1">Exemple Trailing Stop 5% :</p>
            <p>Entree: $100 -> SL: $95</p>
            <p>Monte: $120 -> SL remonte: $114</p>
            <p>Monte: $150 -> SL remonte: $142.50</p>
            <p>Descend: $142 -> FERME -> <span className="text-emerald-600 font-bold">Gain +42%</span></p>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card className="rounded-sm">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-slate-500">
            <Lightning size={16} className="inline mr-2" />
            Filtrage Anti-Faux Signaux
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            {sections[7].items.map((item, i) => (
              <div key={i} className="flex items-center gap-2 p-1.5">
                <ShieldCheck size={14} className="text-emerald-500 flex-shrink-0" />
                <p className="text-xs font-body text-slate-600">{item}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
